import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { bookService } from "./Nav";

const BUILDS = [
    {
        n: "01",
        name: "All American Notice",
        type: "Notice & lien platform — live at allamericannotice.com",
        img: "/assets/build-notice.jpeg",
        link: "https://allamericannotice.com",
        story: "A national platform for contractors, live in production and serving thousands of construction companies — automated preliminary notices, mechanic's liens, and bond claims across all 50 states, with print-and-mail workflows and certified postage built in.",
    },
    {
        n: "02",
        name: "Accounting Firm Client Manager",
        type: "Client manager, secure document portal & password vault — live preview",
        img: "/assets/build-accounting.jpeg",
        link: "https://trusted-docs-hub-1.preview.emergentagent.com",
        preview: true,
        story: "Currently in build for an accounting firm: a complete client manager with engagement tracking, a secure document portal for sensitive tax files, and an encrypted password vault for client credentials with Bitwarden integration — access controls and audit-friendly organization throughout. Live preview up now with secure sign-in and a demo account.",
    },
    {
        n: "03",
        name: "Pack 2 Perfection",
        type: "Packing & home concierge booking platform — live",
        img: "/assets/build-pack2.jpeg",
        link: "https://pack-booking.emergent.host",
        story: "A veteran-owned packing and home-organizing concierge for Pensacola and the Emerald Coast — white-glove packing, unpacking, staging, and interior rearrangement. What started as phone bookings is now a full booking platform: customers pick a service block and lock in their crew with an online deposit, estimates are free, and realtors get a partner program of their own.",
    },
];

export const Portfolio = () => (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
            <Reveal>
                <p className="flex items-center gap-3 text-xs md:text-sm tracking-[0.25em] uppercase text-ink/50">
                    <span className="h-px w-10 bg-worx inline-block" /> Recent builds
                </p>
                <h2 className="mt-6 font-display font-semibold tracking-tighter leading-[0.95] text-5xl md:text-7xl max-w-4xl">
                    Real software, <span className="text-worx">shipped.</span>
                </h2>
                <p className="mt-6 text-ink/60 max-w-lg text-base md:text-lg leading-relaxed">
                    A few of the business systems designed and built for clients — live and in progress —
                    each one scoped in a free discovery call and quoted before any work began.
                </p>
            </Reveal>

            <div className="mt-16 space-y-16 md:space-y-24">
                {BUILDS.map((b, i) => (
                    <Reveal key={b.n}>
                        <div
                            data-testid={`portfolio-card-${b.n}`}
                            className={`grid md:grid-cols-12 gap-8 md:gap-12 items-center ${i % 2 === 1 ? "" : ""}`}
                        >
                            <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                                {b.inProgress ? (
                                    <div className="border border-ink/10 bg-ink p-4 md:p-8 flex items-center justify-center min-h-[280px] md:min-h-[380px]" data-testid={`portfolio-progress-${b.n}`}>
                                        <div className="text-center">
                                            <img src="/assets/icon-512.png" alt="" className="h-14 w-14 mx-auto mb-5" />
                                            <p className="font-display font-semibold tracking-[0.25em] uppercase text-worx text-sm animate-pulse">In progress</p>
                                            <p className="text-paper/40 text-xs mt-2 tracking-wide">In active development — launching soon</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="border border-ink/10 bg-[#EAE8E0] p-4 md:p-8 overflow-hidden group">
                                        <img
                                            src={b.img}
                                            alt={`${b.name} — ${b.type}`}
                                            data-testid={`portfolio-image-${b.n}`}
                                            className="w-full object-cover shadow-[0_18px_40px_rgba(10,10,10,0.18)] group-hover:scale-[1.02] transition-transform duration-700"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                                <span className="font-display text-worx text-lg">{b.n}</span>
                                <h3 className="mt-2 font-display font-medium tracking-tight text-3xl md:text-4xl">{b.name}</h3>
                                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-ink/50">{b.type}</p>
                                <p className="mt-5 text-ink/70 leading-relaxed">{b.story}</p>
                                {b.link && (
                                    <a
                                        href={b.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`portfolio-link-${b.n}`}
                                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-worx hover:text-ink transition-colors duration-200"
                                    >
                                        {b.preview ? "View live preview" : "Visit live site"} <ArrowUpRight size={14} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>

            <Reveal>
                <div className="mt-20 flex flex-wrap items-center justify-between gap-4 border-t border-ink/10 pt-8">
                    <p className="text-ink/60 max-w-md text-base md:text-lg">
                        Your project could be the next one on this page.
                    </p>
                    <a
                        href="#booking"
                        onClick={bookService("web-dev")}
                        data-testid="portfolio-cta"
                        className="inline-flex items-center gap-2 bg-worx text-white px-8 py-4 font-medium hover:bg-ink transition-colors duration-200"
                    >
                        Book a free dev consult <ArrowUpRight size={16} />
                    </a>
                </div>
            </Reveal>
        </div>
    </section>
);
